import { Navigate } from 'react-router-dom';
import { useIndustryAccess } from '@/hooks/useIndustryAccess';
import { CompanyType } from '@/types/company';
import { Loader2 } from 'lucide-react';
import { Logo } from '@/components/ui/logo';

interface WithIndustryRouteProps {
  children: React.ReactNode;
  requiredDomain: CompanyType | CompanyType[];
  redirectTo?: string;
}

/**
 * Higher-Order Component to protect routes that require specific company types
 * @param requiredDomain - The company type(s) required to access the route
 * @param redirectTo - Optional redirect path (defaults to /dashboard)
 */
export function withIndustryRoute<P extends object>(
  Component: React.ComponentType<P>,
  requiredDomain: CompanyType | CompanyType[],
  redirectTo: string = '/dashboard'
) {
  return function IndustryRouteWrapper(props: P) {
    const { hasAccess, isLoading } = useIndustryAccess(requiredDomain);

    if (isLoading) {
      return (
        <div className="h-screen w-screen flex flex-col items-center justify-center bg-background">
          <div className="animate-pulse mb-8">
            <Logo variant="full" size="lg" />
          </div>
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      );
    }

    if (!hasAccess) {
      return <Navigate to={redirectTo} replace />;
    }

    return <Component {...props} />;
  };
}

/**
 * Component wrapper for industry-specific routes
 * Use this directly in JSX instead of withIndustryRoute HOC
 */
export function IndustryRouteGuard({ 
  children, 
  requiredDomain, 
  redirectTo = '/dashboard' 
}: WithIndustryRouteProps) {
  const { hasAccess, isLoading } = useIndustryAccess(requiredDomain);

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-background">
        <div className="animate-pulse mb-8">
          <Logo variant="full" size="lg" />
        </div>
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

